import React from 'react';
import { View } from 'react-native';

import { useT } from '@transifex/react';

import { useOnboardingSignupUrl } from '../hooks/useOnboardingSignupUrl';
import { ROUTES } from '../utils/constants';
import { testId } from '../utils/testId';

import { TextLink } from './TextLink';
import ThemedText from './ThemedText';
import { useLoginRouter } from '../hooks/useLoginRouter';

const tid = (element: string) => testId('AuthFooter', element);

/**
 * Two footer shapes:
 *   - 'login'     : the auth hub. Shows every alternative sign-in method
 *                   (Forgot / Magic link / SSO) that the user might want
 *                   instead of email+password.
 *   - 'secondary' : any non-hub auth screen. Shows only "Back to login".
 *                   Deliberately does NOT cross-link to sibling flows —
 *                   the user just came from /login and can bounce back
 *                   there for anything else. Keeps every secondary screen
 *                   focused on one job.
 *
 * The "Don't have an account? Sign up" row appears on every variant.
 */
interface AuthFooterProps {
  variant: 'login' | 'secondary';
  onBackToLogin?: () => void;
  onSSOClick?: () => void;
  onMagicLinkClick?: () => void;
  showSSO?: boolean;
  testID?: string;
}

const LoginFooterLinks: React.FC<{
  onSSOClick?: () => void;
  onMagicLinkClick?: () => void;
  showSSO: boolean;
}> = ({ onSSOClick, onMagicLinkClick, showSSO }) => {
  const router = useLoginRouter();
  const t = useT();

  return (
    <>
      <TextLink
        href={ROUTES.FORGOT_PASSWORD}
        onPress={() => router.push(ROUTES.FORGOT_PASSWORD)}
        testID={tid('forgot-password-link')}
      >
        {t('Forgot email or password?')}
      </TextLink>
      {onMagicLinkClick && (
        <TextLink href={ROUTES.MAGIC_LINK} onPress={onMagicLinkClick} testID={tid('magic-link')}>
          {t('Sign in without a password')}
        </TextLink>
      )}
      {showSSO && (
        <TextLink href={ROUTES.SSO} onPress={onSSOClick} testID={tid('sso-link')}>
          {t('Sign in with SSO')}
        </TextLink>
      )}
    </>
  );
};

export const AuthFooter: React.FC<AuthFooterProps> = ({
  variant,
  onBackToLogin,
  onSSOClick,
  onMagicLinkClick,
  showSSO = true,
  testID,
}) => {
  const t = useT();
  const signupUrl = useOnboardingSignupUrl();

  return (
    <View className='mt-6 flex-col items-center gap-5' testID={testID}>
      {variant === 'login' && (
        <LoginFooterLinks
          onSSOClick={onSSOClick}
          onMagicLinkClick={onMagicLinkClick}
          showSSO={showSSO}
        />
      )}

      {variant === 'secondary' && (
        <TextLink href={ROUTES.LOGIN} onPress={onBackToLogin} testID={tid('back-to-login-link')}>
          {t('Back to login')}
        </TextLink>
      )}

      <View className='flex-row items-center mt-6'>
        <ThemedText type='bodySmall' color='dark'>
          {t("Don't have an account?")}{' '}
        </ThemedText>
        <TextLink href={signupUrl} testID={tid('sign-up-link')}>{t('Sign up')}</TextLink>
      </View>
    </View>
  );
};
